import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { MatToolbarModule } from '@angular/material/toolbar';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MaterialModule } from 'src/modules/material/material.module';
import { FlexLayoutModule } from '@angular/flex-layout';
import { RedirectComponent } from './components/redirect/redirect.component';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { TermsDialogComponent } from './components/terms-and-conditions/terms-dialog/terms-dialog.component';
import { PrivacyDialogComponent } from './components/privacy-dialog/privacy-dialog/privacy-dialog.component';
import { ForShoppersComponent } from './components/for-shoppers/for-shoppers.component';
import { HomeComponent } from './components/home/home.component';
import { ForPartnersComponent } from './components/for-partners/for-partners.component';
import { ShopperFaqComponent } from './components/shopper-faq/shopper-faq.component';
import { PartnerFaqComponent } from './components/partner-faq/partner-faq.component';
import { PartnerApplyComponent } from './components/partner-apply/partner-apply.component';
import { ServiceWorkerModule } from '@angular/service-worker';
import { environment } from '../environments/environment';
import { NgxSpinnerModule } from 'ngx-spinner';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { PartnerApplySuccessComponent } from './components/partner-apply/partner-apply-success/partner-apply-success.component';
import { EventService } from './services/event.service';
import { PreviewDocumentComponent } from './components/preview-document/preview-document.component';
import { NotFoundComponent } from './components/not-found/not-found.component';
import { ApiService } from './services/api.service';
import { HttpService } from './services/http.service';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CancelTransactionComponent } from './components/cancel-transaction/cancel-transaction.component';
import { WhereToShopComponent } from './components/where-to-shop/where-to-shop.component';
import { VersionService } from './services/version.service';
import {
  PromptUpdateService,
  CheckForUpdateService,
  HandleUnrecoverableStateService,
} from './services/update.service';
import { FeedbackComponent } from './components/feedback/feedback.component';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatListModule } from '@angular/material/list';
import { RedirectToPortalComponent } from './components/redirect-to-portal/redirect-to-portal.component';
import { GoogleAnalyticsService } from './services/googleAnalytics.service';
import { IPAPIService } from './services/ipapi.service';
import { ScrollAnchorDirective } from './directives/ScrollAnchorDirective';
import { ScrollManagerDirective } from './directives/ScrollManagerDirective';
import { ScrollSectionDirective } from './directives/ScrollSectionDirective';
import { SpendLimitComponent } from './components/for-shoppers/spend-limit/spend-limit.component';
import { PartnerApplySnipComponent } from './components/partner-apply/partner-apply-snip/partner-apply-snip.component';
import { PartnerSignupService } from './components/partner-apply/partner-apply-snip/partner-signup.service';
import { NgxCaptchaModule } from 'ngx-captcha';
import { MarketingDownloadsComponent } from './components/marketing-downloads/marketing-downloads.component';
import { BenefitsComponent } from './components/home/benefits/benefits.component';
import { PaymentExplanationComponent } from './components/for-shoppers/payment-explanation/payment-explanation.component';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import {
  LazyLoadImageModule,
  LAZYLOAD_IMAGE_HOOKS,
  ScrollHooks,
} from 'ng-lazyload-image';
import { MarketingMaterialComponent } from './components/marketing-material/marketing-material.component'; // <-- import it
import { MarketingAPI } from './services/marketing/marketing.api';
import { ZeroPayAPI } from './services/zeropay/zeropay.api';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { MatChipsModule } from '@angular/material/chips';
import { ScrollToModule, ScrollToService } from '@nicky-lenaers/ngx-scroll-to';
import { MarketingMaterialViewComponent } from './components/marketing-material/marketing-material-view/marketing-material-view.component';

import { ApiKeyInterceptor } from './intercepts/api_key.intercept';
import { CompetitionsComponent } from './components/competitions/competitions.component';
import { FeaturedWhereToShopComponent } from './components/home/featured-where-to-shop/featured-where-to-shop.component';
import { MerchantBenefitsComponent } from './components/merchant-benefits/merchant-benefits.component';
import { FvrZeropaySupportPopupService } from './components/zeropay-support/fvr-zeropay-support-popup.service';
import { FvrZeropaySupportPopupComponent } from './components/zeropay-support/fvr-zeropay-support-popup.component';
import { DialogEmailComponent } from './components/zeropay-support/dialog-email/dialog-email.component';
import { AccountDeletionComponent } from './components/account-deletion/account-deletion.component';
import { ApiModule } from './api/client/api.module';

@NgModule({
  declarations: [
    AppComponent,
    RedirectComponent,
    TermsDialogComponent,
    PrivacyDialogComponent,
    ForShoppersComponent,
    HomeComponent,
    ForPartnersComponent,
    ShopperFaqComponent,
    PartnerFaqComponent,
    PartnerApplyComponent,
    PartnerApplySuccessComponent,
    PreviewDocumentComponent,
    NotFoundComponent,
    CancelTransactionComponent,
    WhereToShopComponent,
    FeedbackComponent,
    RedirectToPortalComponent,
    ScrollAnchorDirective,
    ScrollSectionDirective,
    ScrollManagerDirective,
    SpendLimitComponent,
    PartnerApplySnipComponent,
    MarketingDownloadsComponent,
    BenefitsComponent,
    PaymentExplanationComponent,
    MarketingMaterialComponent,
    MarketingMaterialViewComponent,
    CompetitionsComponent,
    FeaturedWhereToShopComponent,
    MerchantBenefitsComponent,
    FvrZeropaySupportPopupComponent,
    DialogEmailComponent,
    AccountDeletionComponent,
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    FormsModule,
    ReactiveFormsModule,
    MaterialModule,
    FlexLayoutModule,
    HttpClientModule,
    NgxSpinnerModule,
    PdfViewerModule,
    MatToolbarModule,
    NgxCaptchaModule,
    MatChipsModule,
    MatListModule,
    MatGridListModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.production,
      registrationStrategy: 'registerWhenStable:30000',
    }),
    LazyLoadImageModule,
    ClipboardModule,
    ScrollToModule,
    PdfViewerModule,
    ApiModule.forRoot({
      rootUrl: environment.products.host,
    }),
  ],
  providers: [
    EventService,
    ApiService,
    HttpService,
    VersionService,
    PromptUpdateService,
    CheckForUpdateService,
    HandleUnrecoverableStateService,
    IPAPIService,
    PartnerSignupService,
    { provide: LAZYLOAD_IMAGE_HOOKS, useClass: ScrollHooks },
    MarketingAPI,
    ZeroPayAPI,
    ScrollToService,
    FvrZeropaySupportPopupService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiKeyInterceptor,
      multi: true,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
